import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Shield, AlertTriangle, CheckCircle } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useRoles } from '../hooks/useRoles';

interface TeamMember {
  id: number;
  firstname: string;
  lastname: string;
  workgrouproleid?: number | null;
}

interface ChangeMemberRoleProps {
  isOpen: boolean;
  onClose: () => void;
  member: TeamMember | null;
  onSuccess?: () => void;
  updateMemberRole: (memberId: number, roleId: number) => Promise<any>;
}

export function ChangeMemberRole({ isOpen, onClose, member, onSuccess, updateMemberRole }: ChangeMemberRoleProps) {
  const { data: roles, loading: rolesLoading } = useRoles();
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setSelectedRoleId(member?.workgrouproleid ? String(member.workgrouproleid) : '');
      setError(null);
      setIsComplete(false);
    }
  }, [isOpen, member?.id]);

  const handleSave = async () => {
    if (!member || !selectedRoleId) return;

    setIsProcessing(true);
    setError(null);

    try {
      const response = await updateMemberRole(member.id, Number(selectedRoleId));

      if (response.success) {
        setIsProcessing(false);
        setIsComplete(true);
        onSuccess?.();
        setTimeout(() => {
          onClose();
          setIsComplete(false);
        }, 1200);
      } else {
        throw new Error(response.message || 'Failed to update role');
      }
    } catch (err) {
      setIsProcessing(false);
      setError(err instanceof Error ? err.message : 'Failed to update role. Please try again.');
    }
  };

  const handleClose = () => {
    setError(null);
    setIsComplete(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader className="border-b pb-4">
          <DialogTitle className="flex items-center space-x-2">
            <Shield className="w-5 h-5 text-blue-600" />
            <span>Change Role</span>
          </DialogTitle>
          <DialogDescription>
            Choose a role for {member?.firstname} {member?.lastname}. Their permissions in this workgroup will update immediately.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-4">
          {isComplete ? (
            <div className="text-center py-6">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">Role updated successfully.</p>
            </div>
          ) : (
            <>
              <Select value={selectedRoleId} onValueChange={setSelectedRoleId} disabled={rolesLoading || isProcessing}>
                <SelectTrigger>
                  <SelectValue placeholder={rolesLoading ? 'Loading roles...' : 'Select a role'} />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((role) => (
                    <SelectItem key={role.id} value={String(role.id)}>
                      {role.rolename}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}
            </>
          )}
        </div>

        {!isComplete && (
          <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
            <Button type="button" variant="outline" onClick={handleClose} disabled={isProcessing}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!selectedRoleId || isProcessing}>
              {isProcessing ? 'Saving...' : 'Save'}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
